/**
 * Quiz Page
 * Displays one question at a time with a bottom sheet for answer feedback.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchQuizQuestionsThunk, submitAnswerThunk, checkQuizStatusThunk, resetQuizState } from "@/store/slices/quizSlice";
import { useToast } from "@/hooks/use-toast";

export default function QuizPage() {
  const navigate = useNavigate();
  const { meetingId, quizId } = useParams<{ meetingId: string; quizId: string }>();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const { questions, answers, loading, error, status, meetingId: stateMeetingId } = useAppSelector((state) => state.quiz);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingSelection, setPendingSelection] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasCheckedStatus = useRef(false);

  // Reset state if meetingId changes
  useEffect(() => {
    if (meetingId && stateMeetingId && stateMeetingId !== meetingId) {
      dispatch(resetQuizState(meetingId));
      hasCheckedStatus.current = false;
    }
  }, [meetingId, stateMeetingId, dispatch]);

  // Check status and poll if processing
  useEffect(() => {
    if (!meetingId) return;

    // Initial check if not started
    if (status === "not_started" && !hasCheckedStatus.current) {
      hasCheckedStatus.current = true;
      dispatch(checkQuizStatusThunk(meetingId));
      return;
    }

    if (status === "processing") {
      const interval = setInterval(() => {
        dispatch(checkQuizStatusThunk(meetingId));
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [meetingId, status, dispatch]);

  // Fetch questions when completed
  useEffect(() => {
    if (meetingId && status === "completed" && questions.length === 0 && !error) {
      dispatch(fetchQuizQuestionsThunk(meetingId));
    }
  }, [meetingId, status, questions.length, error, dispatch]);

  const handleOptionClick = useCallback(
    async (optionIndex: number, isAnswered: boolean) => {
      if (isAnswered || sheetOpen || isSubmitting || !questions[currentIndex]) return;
      
      setPendingSelection(optionIndex);
      setIsSubmitting(true);

      const question = questions[currentIndex];
      const selectedAlternative = question.alternatives[optionIndex];

      const resultAction = await dispatch(
        submitAnswerThunk({
          question_id: question.id,
          selected_alternative: selectedAlternative,
        })
      );

      setIsSubmitting(false);

      if (submitAnswerThunk.fulfilled.match(resultAction)) {
        setSheetOpen(true);
      } else {
        setPendingSelection(null);
        toast({
          title: "Error",
          description: "Failed to submit answer. Please try again.",
          variant: "destructive",
        });
      }
    },
    [sheetOpen, isSubmitting, questions, currentIndex, dispatch, toast]
  );

  const handleNext = useCallback(
    (totalQuestions: number) => {
      setPendingSelection(null);
      setSheetOpen(false);

      if (Object.keys(answers).length === totalQuestions) {
        const correctCount = Object.values(answers).filter((a) => a.is_correct).length;
        navigate(`/meeting/${meetingId}/quiz/${quizId}/results`, {
          state: { correctCount, total: totalQuestions },
        });
        return;
      }

      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex((i) => i + 1);
      }
    },
    [answers, navigate, meetingId, quizId, currentIndex]
  );

  if ((loading || status === "processing" || (status === "not_started" && !hasCheckedStatus.current) || (status === "completed" && questions.length === 0 && !error)) && questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">
            {status === "processing" ? "Generating quiz..." : "Loading quiz..."}
          </p>
        </div>
      </div>
    );
  }

  if (error || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">{error || "Quiz not found."}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const question = questions[currentIndex];
  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const currentAnswer = answers[question.id];
  const isAnswered = !!currentAnswer;
  
  // Determine if the pending selection is correct based on the API response
  const isCorrect = currentAnswer?.is_correct;

  const getOptionStyle = (optionIndex: number) => {
    const optionText = question.alternatives[optionIndex];

    if (sheetOpen && pendingSelection !== null) {
      if (optionIndex === pendingSelection) {
        return isCorrect
          ? "border-green-500 bg-green-500/10 text-green-700 dark:text-green-400"
          : "border-destructive bg-destructive/10 text-destructive";
      }
      if (optionText === currentAnswer?.correct_alternative && !isCorrect) {
        return "border-green-500 bg-green-500/10 text-green-700 dark:text-green-400";
      }
      return "border-border opacity-50";
    }

    if (isAnswered) {
      if (optionText === currentAnswer.selected_alternative) {
        return currentAnswer.is_correct
          ? "border-green-500 bg-green-500/10 text-green-700 dark:text-green-400"
          : "border-destructive bg-destructive/10 text-destructive";
      }
      if (
        optionText === currentAnswer.correct_alternative &&
        currentAnswer.selected_alternative !== currentAnswer.correct_alternative
      ) {
        return "border-green-500 bg-green-500/10 text-green-700 dark:text-green-400";
      }
      return "border-border opacity-50";
    }

    return "border-border hover:border-primary hover:bg-primary/5 cursor-pointer";
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border/40 bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/dashboard`)}
            className="gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Exit Quiz
          </Button>
          <span className="text-sm font-medium text-muted-foreground">
            {currentIndex + 1} / {totalQuestions}
          </span>
          <div className="w-16" />
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-muted">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question area */}
      <main className="flex-1 container mx-auto px-4 py-8 max-w-2xl">
        <div className="space-y-8">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Question {currentIndex + 1}
            </p>
            <h2 className="text-xl font-bold text-foreground leading-snug">
              {question.question}
            </h2>
          </div>

          <div className="space-y-3">
            {question.alternatives.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleOptionClick(idx, isAnswered)}
                disabled={isAnswered || sheetOpen || isSubmitting}
                className={cn(
                  "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 text-sm font-medium",
                  getOptionStyle(idx)
                )}
              >
                <span className="inline-flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs font-bold shrink-0">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {option}
                </span>
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* Previous / Next navigation */}
      <div className="container mx-auto px-4 pb-6 max-w-2xl flex justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
          disabled={currentIndex === 0 || sheetOpen}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (currentIndex === totalQuestions - 1 && answeredCount === totalQuestions) {
              handleNext(totalQuestions);
            } else {
              setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1));
            }
          }}
          disabled={(currentIndex === totalQuestions - 1 && answeredCount !== totalQuestions) || sheetOpen}
        >
          {currentIndex === totalQuestions - 1 && answeredCount === totalQuestions ? "See Results" : "Next"}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Bottom Sheet Overlay */}
      {sheetOpen && (
        <div className="fixed inset-0 z-40 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative z-50 bg-background rounded-t-2xl shadow-2xl border-t border-border animate-in slide-in-from-bottom duration-300 p-6 space-y-4 max-w-2xl w-full mx-auto">
            <div className="flex items-center gap-3">
              {isCorrect ? (
                <CheckCircle2 className="w-7 h-7 text-green-500 shrink-0" />
              ) : (
                <XCircle className="w-7 h-7 text-destructive shrink-0" />
              )}
              <div>
                <p
                  className={cn(
                    "text-lg font-bold",
                    isCorrect
                      ? "text-green-600 dark:text-green-400"
                      : "text-destructive"
                  )}
                >
                  {isCorrect ? "Correct!" : "Not quite!"}
                </p>
                {!isCorrect && (
                  <p className="text-sm text-muted-foreground">
                    The correct answer is{" "}
                    <span className="font-semibold text-foreground">
                      "{currentAnswer?.correct_alternative}"
                    </span>
                  </p>
                )}
              </div>
            </div>

            <p className="text-sm text-foreground leading-relaxed bg-muted/30 rounded-lg p-3">
              {currentAnswer?.explanation}
            </p>

            <Button
              className="w-full"
              size="lg"
              onClick={() => handleNext(totalQuestions)}
            >
              {answeredCount === totalQuestions ? "See Results" : "Next"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
